import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Client } from '../../../../types';
import FormHeader from '../../../Company/СompanyForm/FormHeader/FormHeader';
import FormSection from '../../../Company/СompanyForm/FormSection/FormSection';
import ClientDetailsModal from '../../../Customer/ClientDetailsModal/ClientDetailsModal';
import { getFormattedDuration, TranslationHistoryItem } from '../../helpers';
import style from './CallDetailsModal.module.css';

interface CallDetailsModalProps {
  item: TranslationHistoryItem;
  onClose: () => void;
}

const fmtDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD.MM.YYYY') : '—';
};
const fmtTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format('HH:mm') : '—';
};

// Іконки секцій (fill-based — .sectionIcon svg { fill:#0a8081 } робить їх бірюзовими).
const PeopleGlyph = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
  </svg>
);
const PhoneGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.18z" />
  </svg>
);
const StarGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l7.1-1.01L12 2z" />
  </svg>
);

const Stars: React.FC<{ value: number }> = ({ value }) => (
  <span className={style.stars} aria-label={`${value} з 5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <span key={n} className={n <= value ? style.starOn : style.starOff}>
        ★
      </span>
    ))}
  </span>
);

// Поле у стилі картки компанії: назва над «інпут»-боксом зі значенням.
const Field: React.FC<{
  label: string;
  children: React.ReactNode;
  wide?: boolean;
  title?: string;
}> = ({ label, children, wide, title }) => (
  <div className={`${style.field} ${wide ? style.fieldWide : ''}`}>
    <span className={style.fieldLabel}>{label}</span>
    <div className={style.fieldBox} title={title}>
      <span className={style.fieldValue}>{children}</span>
    </div>
  </div>
);

const CallDetailsModal: React.FC<CallDetailsModalProps> = ({
  item,
  onClose,
}) => {
  const navigate = useNavigate();
  const [showClientCard, setShowClientCard] = useState(false);

  const translatorName = item.translator?.userName || '—';
  const isGuest = !item.client && !!item.external;
  const counterpartName =
    item.client?.userName || item.external?.userName || '—';

  // Повна картка клієнта (як «картка клієнта») — будуємо з даних, що прийшли з дзвінка.
  const clientForCard: Client | null = item.client
    ? {
        id: item.client.id,
        userName: item.client.userName,
        email: item.clientEmail ?? '',
        phone: item.clientPhone ?? '',
        avatarUrl: item.client.avatarUrl ?? '',
        plan: '',
        createdAt: item.clientCreatedAt ?? undefined,
        lastLoginAt: item.clientLastLoginAt ?? undefined,
        emailConfirmed: item.clientEmailConfirmed ?? undefined,
        comment: item.clientComment ?? '',
      }
    : null;

  const handleCompanyClick = () => {
    if (!item.companyOwnerId) return;
    navigate(`/company/${item.companyOwnerId}`);
  };

  const handleWrapperClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <>
      <div className={style.formWrapper} onClick={handleWrapperClick}>
        <div className={style.modalWrap}>
          <FormHeader title="Картка дзвінка" onClose={onClose} />

          <div className={style.body}>
            <FormSection
              title="Учасники"
              subtitle="Перекладач і співрозмовник"
              icon={<PeopleGlyph />}
            >
              <div className={style.row}>
                <Field label="Перекладач" title={translatorName}>
                  {translatorName}
                </Field>
                <Field
                  label={isGuest ? 'Гість' : 'Клієнт'}
                  title={counterpartName}
                >
                  {clientForCard ? (
                    <button
                      type="button"
                      className={style.linkVal}
                      onClick={() => setShowClientCard(true)}
                      title="Відкрити картку клієнта"
                    >
                      {counterpartName}
                    </button>
                  ) : (
                    counterpartName
                  )}
                </Field>
                <Field
                  label="Компанія"
                  title={item.companyName || 'Без договору'}
                >
                  {item.companyName && item.companyOwnerId ? (
                    <button
                      type="button"
                      className={style.linkVal}
                      onClick={handleCompanyClick}
                      title="Перейти до картки компанії"
                    >
                      {item.companyName}
                    </button>
                  ) : item.companyName ? (
                    item.companyName
                  ) : (
                    <span className={style.muted}>Без договору</span>
                  )}
                </Field>
              </div>
            </FormSection>

            <FormSection
              title="Деталі дзвінка"
              subtitle="Час, тривалість і статус"
              icon={<PhoneGlyph />}
            >
              <div className={style.row}>
                <Field label="Дата">{fmtDate(item.createdAt)}</Field>
                <Field label="Час початку">{fmtTime(item.createdAt)}</Field>
                <Field label="Прийнято">{fmtTime(item.acceptedAt)}</Field>
                <Field label="Завершено">{fmtTime(item.finishedAt)}</Field>
                <Field label="Тривалість">
                  {getFormattedDuration(item.callDuration)}
                </Field>
                <Field label="Тип">
                  {item.isScheduled ? 'За заявкою' : 'Миттєвий'}
                </Field>
                <Field label="Ким завершено">{item.finishedBy || '—'}</Field>
                <Field label="Статус">{item.status || 'Завершено'}</Field>
              </div>
              {item.comment && (
                <div className={style.row}>
                  <Field label="Коментар до дзвінка" wide title={item.comment}>
                    {item.comment}
                  </Field>
                </div>
              )}
            </FormSection>

            {typeof item.rating === 'number' && item.rating > 0 && (
              <FormSection
                title="Оцінка"
                subtitle="Оцінка дзвінка клієнтом"
                icon={<StarGlyph />}
              >
                <div className={style.row}>
                  <Field label="Оцінка">
                    <span className={style.ratingCell}>
                      <Stars value={item.rating} />
                      <span className={style.ratingNum}>{item.rating} / 5</span>
                    </span>
                  </Field>
                  <Field label="Причина" title={item.ratingReason || '—'}>
                    {item.ratingReason || '—'}
                  </Field>
                  <Field
                    label="Коментар до оцінки"
                    title={item.ratingComment || '—'}
                  >
                    {item.ratingComment || '—'}
                  </Field>
                </div>
              </FormSection>
            )}
          </div>
        </div>
      </div>

      {showClientCard && clientForCard && (
        <ClientDetailsModal
          client={clientForCard}
          onClose={() => setShowClientCard(false)}
        />
      )}
    </>
  );
};

export default CallDetailsModal;
